<script setup>
import { onMounted, watch, computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { initFlowbite } from 'flowbite'
import Pusher from 'pusher-js'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useBatchStore } from '@/stores/BatchStore'
import { useUserStore } from './stores/UserStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut'
import MainLayout from '@/components/MainLayout/index.vue'
import GlobalVideoWidget from '@/components/GlobalVideoWidget.vue'
import NewVersionToast from '@/components/NewVersionToast.vue'
import ListingsExportToast from '@/components/ListingsExportToast.vue'
import * as Sentry from "@sentry/browser"
import { COLLX_CHANNEL } from '@/constants'

const route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const globalStore = useGlobalStore()
const batchStore = useBatchStore()
const connectionStore = useConnectionStore()

// XXX TODO bleh. this is sloppy. use meta on route.
const isAuthLayout = computed(() => {
  if (
    route.name === 'SignIn' ||
    route.name === 'SignUpEmail' ||
    route.name === 'Verification' ||
    route.name === 'ResetVerification' ||
    route.name === 'AccountCreation' ||
    route.matched[0]?.name === 'AccountCreation' ||
    route.name === 'ResetPassword' ||
    route.name === 'EmailSent' ||
    route.name === 'LinkExpired' ||
    route.name === 'SetPassword' ||
    route.name === 'NotFoundPage'
  ) {
    return true
  } else {
    return false
  }
})

const user = computed(() => userStore.user)

useKeyboardShortcut(router)

globalStore.fetchBuildTxt()

setInterval(() => {
  globalStore.fetchBuildTxt()
}, 300000)

onMounted(async () => {
  globalStore.windowWidth = window.innerWidth
  globalStore.windowHeight = window.innerHeight
  window.addEventListener('resize', () => {
    globalStore.windowWidth = window.innerWidth
    globalStore.windowHeight = window.innerHeight

    if (window.innerWidth < 1280) {
      globalStore.sidebar = false
    }
  })

  // initialize components based on data attribute selectors
  initFlowbite();

  const params = new URLSearchParams(window.location.search)
  const authAs = params.get('authAs')
  const sessionOverrideAuthToken = sessionStorage.getItem('cdp_auth_override_token')

  if (authAs || sessionOverrideAuthToken) {
    await userStore.checkSession(authAs || sessionOverrideAuthToken)
    console.log('logged in as', userStore.user?.email)
    router.replace('/')
  } else {
    userStore.checkSession()
  }

  if (navigator.userAgent.search('Mac') !== -1) {
    globalStore.userAgent = 'cmd'
  } else {
    globalStore.userAgent = 'ctrl'
  }
  globalStore.sidebar =  JSON.parse(localStorage.getItem('sidebar') || false)
  localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
})

watch(user, () => {
  userStore.user && batchStore.fetchConfigs({ withoutLoader: true })
  userStore.user && Sentry.setUser(userStore.user)
})

watch(
  () => userStore.loggedIn,
  (loggedIn, prevLoggedIn) => {
    if (!prevLoggedIn && loggedIn) {
      // ...
      // Enable pusher logging - don't include this in production
      Pusher.logToConsole = true;

      var pusher = new Pusher('f1107eef8ca40861ee35', {
        cluster: 'mt1'
      });

      var channel = pusher.subscribe(`channel-${userStore.user?.id}`);

      channel.bind('batchUpdate', function(data) {        
        if (
          data?.Event === 'card_processing_completed' || 
          data?.Event === 'imort_cert_processing_completed' || 
          data?.Event === 'imort_cert_processing_failed' || 
          data?.Event === 'card_processing_failed' || 
          data?.Event === 'rotate_processing_completed'
        ) {
          batchStore.updateCard(data.CardId, data?.Event)
        }

        if (data?.Event === 'replace_card_processing_completed') {
          batchStore.updateCard(data.CardId, data?.Event)
          setTimeout(() => {
            batchStore.replaceImageLoader = false
          }, 0)
        }

        if ((data?.Event === 'remove_banner_completed' || data?.Event === 'add_banner_completed') && data.LastCard) {
          batchStore.fetchSelectedBatchCards(null, false)
        }

        if (data?.Event === 'swap_processing_completed') {
          batchStore.updateCard(data.CardId, data?.Event)

          if (data.LastCard) {
            batchStore.updateSwappedToast(true, 'Image swap complete!')
          }
        }

        if (data?.Event === 'card_reprocessing_completed' && data.LastCard) {
          batchStore.fetchSelectedBatchCards(null, false)
        }

        if (data?.Event === 'scan_counts_updated') {
          batchStore.config = {
            ...batchStore.config,
            availableScans: data.AvailableScans,
            totalScans: data.TotalScans
          }
        }
      })

      channel.bind('collx_import_completed', function(data) {
        connectionStore.showExportToast = true
        connectionStore.exportedChannelInfo = {...COLLX_CHANNEL, label: 'Listings published', message: 'cards have been listed on CollX.', ...data}
      
        if (route.params.batchId) {
          batchStore.fetchSelectedBatchCards(null, false)
        }
      })
    }
  }
)

watch(route, () => {
  if (route.path === '/batches') {
    zE('webWidget', 'updateSettings', {
      webWidget: {
        offset: { horizontal: '75px', vertical: '55px' }
      }
    })
  } else {
    zE('webWidget', 'updateSettings', {
      webWidget: {
        offset: { horizontal: '5px', vertical: '5px' }
      }
    })
  }
})
</script>

<template>
  <!-- <RouterView v-if="isAuthLayout || batchStore.config?.subscription?.plan_id" /> -->
  <NewVersionToast />
  <ListingsExportToast />

  <RouterView v-if="isAuthLayout" />
  <template v-else>
    <MainLayout />

    <GlobalVideoWidget />
  </template>
</template>